import { type ComponentType } from "react"
import {
    createBrowserRouter,
    isRouteErrorResponse,
    matchRoutes,
    redirect,
    useLocation,
    useRouteError,
    type LoaderFunctionArgs,
    type ShouldRevalidateFunctionArgs,
} from "react-router"
import { Button } from "@/components/ui/button"
import { AlertCircle, Home, RefreshCw } from "@/components/icons"
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"
import autopartsApi, {
    AutopartsApiError,
    normalizeAuthenticatedUser,
    normalizePageResult,
    normalizeProductView,
    fetchProductById,
    fetchProducts,
    fetchPlatformStats,
    refreshMarketplaceData,
    normalizeWishlistItemView,
    clearStoredAuthSession,
    readStoredAuthSession,
    updateStoredAuthSessionUser,
    type AuthenticatedUser,
    type BuyerReviewView,
    type OrderView,
    type PlatformStats,
    type Product,
    type PageQuery,
    type PageResult,
    type ProductView,
    type UserView,
    type VendorProfileView,
    type WishlistItemView,
} from "./api/autoparts"

import { Loading } from "./components/loading"
import { Seo } from "./components/seo"

type SessionRole = "buyer" | "seller" | "courier" | "quality_assessor" | "admin"

type SessionSnapshot = {
    isAuthenticated: boolean
    role?: SessionRole
    mustChangePassword?: boolean
    requiresEmailVerification?: boolean
}

type RouteGuardOptions = {
    guestOnly?: boolean
    allowedRoles?: SessionRole[]
}

type RouteLoader = (args: LoaderFunctionArgs) => Promise<unknown> | unknown

const ROUTE_CHUNK_RELOAD_KEY = "sparelynk:route-chunk-reload"

function getRouteChunkReloadTarget(): string | null {
    try {
        return window.sessionStorage.getItem(ROUTE_CHUNK_RELOAD_KEY)
    } catch {
        return null
    }
}

function setRouteChunkReloadTarget(value: string): boolean {
    try {
        window.sessionStorage.setItem(ROUTE_CHUNK_RELOAD_KEY, value)
        return true
    } catch {
        return false
    }
}

function clearRouteChunkReloadTarget() {
    try {
        window.sessionStorage.removeItem(ROUTE_CHUNK_RELOAD_KEY)
    } catch {
        // Storage can be unavailable in restricted browser modes.
    }
}

function isRouteChunkLoadError(error: unknown): boolean {
    if (!(error instanceof Error)) {
        return false
    }

    return /failed to fetch dynamically imported module|importing a module script failed|chunkloaderror|loading chunk [\w-]+ failed/i.test(
        error.message,
    )
}

function lazyRouteComponent(
    importer: () => Promise<unknown>,
    exportName: string
) {
    return async () => {
        let routeModule: Record<string, unknown>

        try {
            routeModule = (await importer()) as Record<string, unknown>
            if (typeof window !== "undefined") {
                clearRouteChunkReloadTarget()
            }
        } catch (error) {
            if (
                typeof window !== "undefined" &&
                isRouteChunkLoadError(error)
            ) {
                const reloadTarget = `${window.location.pathname}${window.location.search}`
                const previousReloadTarget = getRouteChunkReloadTarget()

                if (
                    previousReloadTarget !== reloadTarget &&
                    setRouteChunkReloadTarget(reloadTarget)
                ) {
                    window.location.reload()
                    return await new Promise<never>(() => undefined)
                }
            }

            throw error
        }

        const Component = routeModule[exportName]

        if (!Component) {
            throw new Error(`Missing route component export: ${exportName}`)
        }

        return {
            Component: Component as ComponentType,
        }
    }
}

function RouterHydrateFallback() {
    return (
        <div className="flex min-h-screen items-center justify-center bg-background">
            <Loading />
        </div>
    )
}

let sessionSnapshotPromise: Promise<SessionSnapshot> | null = null

function toPositiveNumber(value: string | null): number | undefined {
    if (!value) return undefined

    const parsed = Number(value)
    if (!Number.isFinite(parsed) || parsed <= 0) {
        return undefined
    }

    return Math.floor(parsed)
}

function getPageQuery(request: Request): PageQuery {
    const url = new URL(request.url)
    const sortDirection = url.searchParams.get("sortDirection")

    return {
        page: toPositiveNumber(url.searchParams.get("page")),
        pageSize: toPositiveNumber(url.searchParams.get("pageSize")),
        search: url.searchParams.get("search") || undefined,
        sortBy: url.searchParams.get("sortBy") || undefined,
        sortDirection:
            sortDirection === "asc" || sortDirection === "desc"
                ? sortDirection
                : undefined,
    }
}

const MESSAGE_PANEL_SEARCH_PARAMS = [
    "messages",
    "channel",
    "conversationId",
    "conversation_id",
    "buyerId",
    "buyer_id",
    "vendorId",
    "vendor_id",
    "sellerId",
    "seller_id",
    "buyerName",
    "buyer_name",
    "sellerName",
    "seller_name",
    "sellerAvatarUrl",
    "seller_avatar_url",
    "orderId",
    "order_id",
    "productId",
    "product_id",
    "contextType",
    "context_type",
    "contextId",
    "context_id",
    "contextLabel",
    "context_label",
    "contextImage",
    "context_image",
] as const

function getNonMessagePanelSearch(url: URL): string {
    const searchParams = new URLSearchParams(url.search)

    for (const key of MESSAGE_PANEL_SEARCH_PARAMS) {
        searchParams.delete(key)
    }

    searchParams.sort()
    return searchParams.toString()
}

function shouldRevalidateMessagePanelNavigation({
    currentUrl,
    nextUrl,
    formMethod,
    defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
    if (
        currentUrl.pathname !== nextUrl.pathname ||
        (formMethod && formMethod.toUpperCase() !== "GET") ||
        currentUrl.search === nextUrl.search
    ) {
        return defaultShouldRevalidate
    }

    return getNonMessagePanelSearch(currentUrl) ===
        getNonMessagePanelSearch(nextUrl)
        ? false
        : defaultShouldRevalidate
}

function requireRouteParam(
    params: LoaderFunctionArgs["params"],
    key: string
): string {
    const value = params[key]

    if (!value) {
        throw new Response(`Missing route parameter: ${key}`, {
            status: 404,
            statusText: "Not Found",
        })
    }

    return value
}

function normalizeSessionRole(value: unknown): SessionRole | undefined {
    if (typeof value !== "string") {
        return undefined
    }

    const normalized = value.trim().toLowerCase()

    if (
        normalized === "buyer" ||
        normalized === "customer" ||
        normalized === "customer_user"
    ) {
        return "buyer"
    }

    if (
        normalized === "seller" ||
        normalized === "vendor" ||
        normalized === "merchant"
    ) {
        return "seller"
    }

    if (
        normalized === "courier" ||
        normalized === "driver" ||
        normalized === "delivery" ||
        normalized === "delivery_driver"
    ) {
        return "courier"
    }

    if (
        normalized === "quality_assessor" ||
        normalized === "quality-assessor" ||
        normalized === "assessor"
    ) {
        return "quality_assessor"
    }

    if (normalized === "admin" || normalized === "administrator") {
        return "admin"
    }

    return undefined
}

function getSessionHomePath(role?: SessionRole): string {
    switch (role) {
        case "admin":
            return "/admin/dashboard"
        case "seller":
            return "/seller/dashboard"
        case "courier":
            return "/courier/deliveries"
        case "quality_assessor":
            return "/quality-assessor/products"
        case "buyer":
        default:
            return "/buyer/home"
    }
}

function requiresPasswordChange(user: Pick<AuthenticatedUser, "role" | "mustChangePassword" | "emailVerified">): boolean {
    return (
        user.role === "admin" &&
        (Boolean(user.mustChangePassword) || user.emailVerified === false)
    )
}

function requiresEmailVerification(user: Pick<AuthenticatedUser, "role" | "emailVerified">): boolean {
    return user.role !== "admin" && user.emailVerified === false
}

function getEmailVerificationPath(user: Pick<AuthenticatedUser, "email" | "role">): string {
    const params = new URLSearchParams()
    params.set("mode", "verify-email-pending")

    if (user.email) {
        params.set("email", user.email)
    }

    params.set("role", user.role)
    return `/auth/verify-email?${params.toString()}`
}

function getPasswordChangePath(user: Pick<AuthenticatedUser, "id" | "email" | "role">): string {
    const params = new URLSearchParams()

    if (user.email) {
        params.set("email", user.email)
    }

    if (user.id) {
        params.set("userId", user.id)
    }

    params.set("role", user.role)

    const query = params.toString()
    return query ? `/auth/change-password?${query}` : "/auth/change-password"
}

function isPasswordChangeRoute(request?: Request): boolean {
    if (!request) return false

    try {
        return new URL(request.url).pathname === "/auth/change-password"
    } catch {
        return false
    }
}

async function getCurrentSessionUser(forceRefresh = false): Promise<AuthenticatedUser | null> {
    const storedSession = readStoredAuthSession()

    if (storedSession?.user && !forceRefresh) {
        return storedSession.user
    }

    try {
        const session = await autopartsApi.auth.getCurrentUser(undefined, {
            forceRefresh: true,
        })
        const currentUser = normalizeAuthenticatedUser(session) ?? readStoredAuthSession()?.user ?? null

        if (currentUser) {
            updateStoredAuthSessionUser(currentUser, session)
            return currentUser
        }
    } catch (error) {
        if (error instanceof AutopartsApiError && error.status === 401) {
            clearStoredAuthSession()
            return null
        }
    }

    return storedSession?.user ?? null
}

function readStoredSessionSnapshot(): SessionSnapshot | null {
    const storedSession = readStoredAuthSession()

    if (!storedSession?.user) {
        return null
    }

    const role = normalizeSessionRole(storedSession.user.role)

    if (!role) {
        clearStoredAuthSession()
        return {
            isAuthenticated: false,
        }
    }

    return {
        isAuthenticated: true,
        role,
        mustChangePassword: requiresPasswordChange(storedSession.user),
        requiresEmailVerification: requiresEmailVerification(storedSession.user),
    }
}

async function readSessionSnapshot(): Promise<SessionSnapshot> {
    const storedSnapshot = readStoredSessionSnapshot()

    if (storedSnapshot) {
        return storedSnapshot
    }

    if (!sessionSnapshotPromise) {
        sessionSnapshotPromise = (async () => {
            try {
                const currentUser = await getCurrentSessionUser()

                if (!currentUser) {
                    return {
                        isAuthenticated: false,
                    }
                }

                const role = normalizeSessionRole(currentUser.role)

                if (!role) {
                    clearStoredAuthSession()
                    return {
                        isAuthenticated: false,
                    }
                }

                return {
                    isAuthenticated: true,
                    role,
                    mustChangePassword: requiresPasswordChange(currentUser),
                    requiresEmailVerification: requiresEmailVerification(currentUser),
                }
            } catch {
                return {
                    isAuthenticated: false,
                }
            }
        })().finally(() => {
            sessionSnapshotPromise = null
        })
    }

    return sessionSnapshotPromise
}

async function getGuardRedirect(options: RouteGuardOptions, request?: Request) {
    if (options.guestOnly) {
        const session = await readSessionSnapshot()

        if (!session.isAuthenticated || !session.role) {
            return null
        }

        if (session.mustChangePassword) {
            const storedSession = readStoredAuthSession()

            if (storedSession?.user) {
                return redirect(getPasswordChangePath(storedSession.user))
            }

            return redirect("/auth/change-password")
        }

        if (session.requiresEmailVerification) {
            const storedSession = readStoredAuthSession()

            if (storedSession?.user) {
                return redirect(getEmailVerificationPath(storedSession.user))
            }

            return redirect("/auth/verify-email?mode=verify-email-pending")
        }

        return redirect(getSessionHomePath(session.role))
    }

    const session = await readSessionSnapshot()

    if (!options.allowedRoles || options.allowedRoles.length === 0) {
        return null
    }

    if (!session.isAuthenticated) {
        return redirect("/auth/login")
    }

    if (!session.role) {
        return redirect("/auth/login")
    }

    if (session.mustChangePassword && !isPasswordChangeRoute(request)) {
        const storedSession = readStoredAuthSession()

        if (storedSession?.user) {
            return redirect(getPasswordChangePath(storedSession.user))
        }

        return redirect("/auth/change-password")
    }

    if (session.requiresEmailVerification) {
        const storedSession = readStoredAuthSession()

        if (storedSession?.user) {
            return redirect(getEmailVerificationPath(storedSession.user))
        }

        return redirect("/auth/verify-email?mode=verify-email-pending")
    }

    if (!options.allowedRoles.includes(session.role)) {
        return redirect(getSessionHomePath(session.role))
    }

    if (session.role === "seller" && isSellerProfileRoute(request)) {
        const seller = await getCurrentSessionUser(true)
        if (
            sellerCredentialsRequireCompletion(seller) &&
            !isSellerCredentialRoute(request)
        ) {
            return redirect("/seller/profile?tab=credentials")
        }
    }

    return null
}

function sellerCredentialsRequireCompletion(user: AuthenticatedUser | null | undefined): boolean {
    if (!user || normalizeSessionRole(user.role) !== "seller") return false
    const status = String(user.sellerVerificationStatus ?? "NOT_SUBMITTED")
        .trim()
        .toUpperCase()
    return !["PENDING", "FULLY_VERIFIED", "VERIFIED"].includes(status)
}

function isSellerCredentialRoute(request?: Request): boolean {
    if (!request) return false
    const url = new URL(request.url)
    return url.pathname === "/seller/profile" && url.searchParams.get("tab") === "credentials"
}

function isSellerProfileRoute(request?: Request): boolean {
    if (!request) return false
    return new URL(request.url).pathname === "/seller/profile"
}

function createGuardedLoader(
    loader?: RouteLoader,
    options: RouteGuardOptions = {}
): RouteLoader {
    return async (args: LoaderFunctionArgs) => {
        const redirectResponse = await getGuardRedirect(options, args.request)

        if (redirectResponse) {
            return redirectResponse
        }

        return loader ? loader(args) : null
    }
}

function createEmptyPageResult<T>(page = 1, pageSize = 10): PageResult<T> {
    return {
        items: [],
        total: 0,
        page,
        pageSize,
        totalPages: 0,
    }
}

function scopeBuyerOrders(
    result: PageResult<OrderView>,
    customerId?: string
): PageResult<OrderView> {
    if (!customerId) {
        return result
    }

    const hasCustomerScopedOrders = result.items.some(
        (order) =>
            typeof order.customerId === "string" &&
            order.customerId.trim().length > 0
    )

    if (!hasCustomerScopedOrders) {
        return result
    }

    const items = result.items.filter((order) => order.customerId === customerId)

    return {
        ...result,
        items,
        total: items.length,
        page: 1,
        totalPages: items.length > 0 ? 1 : 0,
    }
}

function scopeSellerProducts(
    result: PageResult<ProductView>,
    vendorId?: string
): PageResult<ProductView> {
    if (!vendorId) {
        return createEmptyPageResult<ProductView>(
            result.page,
            result.pageSize
        )
    }

    const items = result.items.filter(
        (product) =>
            typeof product.vendorId === "string" &&
            product.vendorId.trim() === vendorId
    )

    return {
        ...result,
        items,
        total: items.length,
        page: 1,
        totalPages: items.length > 0 ? 1 : 0,
    }
}

function scopeSellerOrders(
    result: PageResult<OrderView>,
    vendorId?: string
): PageResult<OrderView> {
    if (!vendorId) {
        return createEmptyPageResult<OrderView>(
            result.page,
            result.pageSize
        )
    }

    const items = result.items.filter(
        (order) =>
            typeof order.vendorId === "string" &&
            order.vendorId.trim() === vendorId
    )

    return {
        ...result,
        items,
        total: items.length,
        page: 1,
        totalPages: items.length > 0 ? 1 : 0,
    }
}

function loadWelcome() {
    const session = readStoredSessionSnapshot()

    if (session?.isAuthenticated && session.role) {
        if (session.mustChangePassword) {
            const storedSession = readStoredAuthSession()
            return redirect(
                storedSession?.user
                    ? getPasswordChangePath(storedSession.user)
                    : "/auth/change-password"
            )
        }

        if (session.requiresEmailVerification) {
            const storedSession = readStoredAuthSession()
            return redirect(
                storedSession?.user
                    ? getEmailVerificationPath(storedSession.user)
                    : "/auth/verify-email?mode=verify-email-pending"
            )
        }

        return redirect(getSessionHomePath(session.role))
    }

    return {
        apiBaseUrl: autopartsApi.health.getBaseUrl(),
    }
}

type PublicPlatformLoaderData = {
    platformStats: PlatformStats | null
}

async function loadPublicPlatformData(): Promise<PublicPlatformLoaderData> {
    return {
        platformStats: await fetchPlatformStats().catch(() => null),
    }
}

type BuyerHomeLoaderData = {
    products: Product[]
    platformStats: PlatformStats | null
}

async function loadBuyerHome(): Promise<BuyerHomeLoaderData> {
    const [products, platformStats] = await Promise.all([
        fetchProducts({
            page: 1,
            pageSize: 64,
            listingState: "LIVE",
            sortBy: "createdAt",
            sortDirection: "desc",
            summary: true,
        }),
        fetchPlatformStats().catch(() => null),
    ])

    return {
        products: products.filter(
            (product) => product.listingState === "live" && product.qualityAssuranceSeal,
        ),
        platformStats,
    }
}

async function loadBuyerProducts(): Promise<Product[]> {
    const products = await fetchProducts({
        page: 1,
        pageSize: 500,
        listingState: "LIVE",
        sortBy: "createdAt",
        sortDirection: "desc",
        summary: true,
    })

    return products.filter(
        (product) => product.listingState === "live" && product.qualityAssuranceSeal,
    )
}

async function loadAdminProducts(): Promise<Product[]> {
    return fetchProducts({
        page: 1,
        pageSize: 500,
        sortBy: "createdAt",
        sortDirection: "desc",
        summary: true,
    })
}

async function loadProductById({ params }: LoaderFunctionArgs): Promise<Product> {
    const product = await fetchProductById(requireRouteParam(params, "id"))

    if (!product || product.listingState !== "live" || !product.qualityAssuranceSeal) {
        throw new Response("Product not found.", {
            status: 404,
            statusText: "Not Found",
        })
    }

    return product
}

type BuyerShopLoaderData = {
    vendorProfile: VendorProfileView | null
    products: Product[]
    reviews: BuyerReviewView[]
}

async function loadBuyerShop({ params }: LoaderFunctionArgs): Promise<BuyerShopLoaderData> {
    const vendorId = requireRouteParam(params, "vendorId")
    const [vendorProfile, products, reviews] = await Promise.all([
        autopartsApi.vendors
            .getById<VendorProfileView>(vendorId)
            .catch(() => null),
        fetchProducts({
            page: 1,
            pageSize: 500,
            vendorId,
            listingState: "LIVE",
            sortBy: "updatedAt",
            sortDirection: "desc",
            summary: true,
        }).catch(() => []),
        autopartsApi.reviews.listShop(vendorId).catch(() => []),
    ])

    return {
        vendorProfile,
        products: products.filter(
            (product) =>
                product.vendorId === vendorId &&
                product.listingState === "live" &&
                product.qualityAssuranceSeal,
        ),
        reviews,
    }
}

async function loadOrders({ request }: LoaderFunctionArgs) {
    const query = getPageQuery(request)
    const currentUser = await getCurrentSessionUser()
    const buyerPage = query.page ?? 1
    const buyerPageSize = query.pageSize ?? 10

    let orders: PageResult<OrderView>

    try {
        orders = await autopartsApi.orders.list<PageResult<OrderView>>({
            ...query,
            page: buyerPage,
            pageSize: buyerPageSize,
            ...(currentUser?.id ? { customerId: currentUser.id } : {}),
        })
    } catch (error) {
        if (error instanceof AutopartsApiError) {
            orders = createEmptyPageResult<OrderView>(buyerPage, buyerPageSize)
        } else {
            throw error
        }
    }

    return {
        currentUser,
        orders: scopeBuyerOrders(orders, currentUser?.id),
    }
}

async function loadBuyerReports() {
    const currentUser = await getCurrentSessionUser()
    const buyerOrders = new Map<string, OrderView>()
    let page = 1
    let totalPages = 1

    try {
        do {
            const result = await autopartsApi.orders.list<PageResult<OrderView>>({
                page,
                pageSize: 100,
                summary: true,
                ...(currentUser?.id ? { customerId: currentUser.id } : {}),
            })
            const scoped = scopeBuyerOrders(result, currentUser?.id)
            scoped.items.forEach((order, index) => {
                buyerOrders.set(order.id || `${page}-${index}`, order)
            })
            totalPages = Math.max(1, result.totalPages || 1)
            page += 1
        } while (page <= totalPages && page <= 100)
    } catch (error) {
        if (!(error instanceof AutopartsApiError)) throw error
    }

    const items = Array.from(buyerOrders.values())
    return {
        currentUser,
        orders: {
            items,
            page: 1,
            pageSize: items.length || 100,
            total: items.length,
            totalPages: items.length > 0 ? 1 : 0,
        },
    }
}

async function loadBuyerConversations() {
    return {
        currentUser: await getCurrentSessionUser(),
    }
}

async function loadOrderById({ params }: LoaderFunctionArgs) {
    const currentUser = await getCurrentSessionUser()
    const order = await autopartsApi.orders.getById<OrderView>(
        requireRouteParam(params, "id")
    )

    if (
        currentUser?.id &&
        typeof order.customerId === "string" &&
        order.customerId.trim().length > 0 &&
        order.customerId !== currentUser.id
    ) {
        throw new Response("You do not have permission to view this order.", {
            status: 403,
            statusText: "Forbidden",
        })
    }

    return {
        currentUser,
        order,
    }
}

async function loadBuyerWishlist({ request }: LoaderFunctionArgs) {
    const query = getPageQuery(request)
    const currentUser = await getCurrentSessionUser()
    const wishlistPage = query.page ?? 1
    const wishlistPageSize = query.pageSize ?? 24

    let rawWishlist: unknown = null

    if (currentUser?.id) {
        try {
            rawWishlist = await autopartsApi.wishlist.listForUser(
                currentUser.id,
                {
                    ...query,
                    page: wishlistPage,
                    pageSize: wishlistPageSize,
                }
            )
        } catch (error) {
            if (!(error instanceof AutopartsApiError)) {
                throw error
            }
        }
    }

    let wishlist = normalizePageResult<WishlistItemView>(
        rawWishlist,
        normalizeWishlistItemView,
        wishlistPage,
        wishlistPageSize
    )

    if (wishlist.items.length === 0 && rawWishlist !== null) {
        const productFallback = normalizePageResult<ProductView>(
            rawWishlist,
            normalizeProductView,
            wishlistPage,
            wishlistPageSize
        )

        if (productFallback.items.length > 0) {
            wishlist = {
                ...productFallback,
                items: productFallback.items.map((product) => ({
                    id: product.id,
                    productId: product.id,
                    product,
                })),
            }
        }
    }

    const resolvedProducts = await Promise.all(
        wishlist.items.map(async (entry) => {
            if (entry.product) {
                return [entry.productId, entry.product] as const
            }

            if (!entry.productId) {
                return null
            }

            try {
                const productResponse = await autopartsApi.products.getById(
                    entry.productId
                )
                const product =
                    normalizeProductView(productResponse) ??
                    (productResponse as ProductView)

                return product ? ([entry.productId, product] as const) : null
            } catch {
                return null
            }
        })
    )

    const productMap = new Map<string, ProductView>()

    for (const item of resolvedProducts) {
        if (item?.[1]) {
            productMap.set(item[0], item[1])
        }
    }

    return {
        currentUser,
        wishlist: {
            ...wishlist,
            items: wishlist.items.map((entry) => ({
                ...entry,
                product: entry.product ?? productMap.get(entry.productId) ?? null,
            })),
        },
    }
}

async function loadBuyerProfile() {
    const currentUser = await getCurrentSessionUser()
    const profilePage = 1
    const profilePageSize = 20

    let user: UserView | null = null
    let orders = createEmptyPageResult<OrderView>(profilePage, profilePageSize)

    if (currentUser?.id) {
        try {
            user = await autopartsApi.users.getById<UserView>(currentUser.id)
            if (user) {
                updateStoredAuthSessionUser({ ...currentUser, ...user })
            }
        } catch (error) {
            if (!(error instanceof AutopartsApiError)) {
                throw error
            }
        }

        try {
            orders = await autopartsApi.orders.list<PageResult<OrderView>>({
                page: profilePage,
                pageSize: profilePageSize,
                customerId: currentUser.id,
                summary: true,
            })
        } catch (error) {
            if (!(error instanceof AutopartsApiError)) {
                throw error
            }
        }
    }

    return {
        currentUser,
        user,
        orders: scopeBuyerOrders(orders, currentUser?.id),
    }
}

type SellerRouteData = {
    currentUser: AuthenticatedUser | null
    vendorProfile: VendorProfileView | null
    products: PageResult<ProductView>
    orders: PageResult<OrderView>
    buyerReviews: BuyerReviewView[]
}

async function loadSellerContext(): Promise<{
    currentUser: AuthenticatedUser | null
    vendorProfile: VendorProfileView | null
}> {
    let currentUser = await getCurrentSessionUser()

    let vendorProfile: VendorProfileView | null = null

    if (currentUser?.id) {
        try {
            const freshUser = await autopartsApi.users.getById<UserView>(currentUser.id)
            currentUser = { ...currentUser, ...freshUser }
            updateStoredAuthSessionUser(currentUser)
        } catch (error) {
            if (!(error instanceof AutopartsApiError)) {
                throw error
            }
        }

        try {
            vendorProfile = await autopartsApi.vendors.getByUserId<VendorProfileView>(
                currentUser.id
            )
        } catch (error) {
            if (
                !(error instanceof AutopartsApiError) ||
                error.status !== 404
            ) {
                throw error
            }
        }
    }

    return {
        currentUser,
        vendorProfile,
    }
}

async function listSellerProductsWithFallback(
    query: PageQuery,
    vendorId?: string,
    fallbackPageSize = 12
): Promise<PageResult<ProductView>> {
    const page = query.page ?? 1
    const pageSize = query.pageSize ?? fallbackPageSize

    if (!vendorId) {
        return createEmptyPageResult<ProductView>(page, pageSize)
    }

    const fetchProducts = (withVendorScope: boolean) =>
        autopartsApi.products.list<PageResult<ProductView>>({
            ...query,
            page,
            pageSize,
            ...(withVendorScope && vendorId ? { vendorId } : {}),
        })

    try {
        const products = await fetchProducts(Boolean(vendorId))
        return vendorId ? scopeSellerProducts(products, vendorId) : products
    } catch (error) {
        if (vendorId && error instanceof AutopartsApiError) {
            const fallback = await fetchProducts(false).catch((fallbackError) => {
                if (fallbackError instanceof AutopartsApiError) {
                    return createEmptyPageResult<ProductView>(page, pageSize)
                }
                throw fallbackError
            })

            return scopeSellerProducts(fallback, vendorId)
        }

        if (error instanceof AutopartsApiError) {
            return createEmptyPageResult<ProductView>(page, pageSize)
        }

        throw error
    }
}

async function listSellerOrdersWithFallback(
    query: PageQuery,
    vendorId?: string,
    fallbackPageSize = 10,
    summary = false
): Promise<PageResult<OrderView>> {
    const page = query.page ?? 1
    const pageSize = query.pageSize ?? fallbackPageSize

    if (!vendorId) {
        return createEmptyPageResult<OrderView>(page, pageSize)
    }

    const fetchOrders = (withVendorScope: boolean) =>
        autopartsApi.orders.list<PageResult<OrderView>>({
            ...query,
            page,
            pageSize,
            summary,
            ...(withVendorScope && vendorId ? { vendorId } : {}),
        })

    try {
        const orders = await fetchOrders(Boolean(vendorId))
        return vendorId ? scopeSellerOrders(orders, vendorId) : orders
    } catch (error) {
        if (vendorId && error instanceof AutopartsApiError) {
            const fallback = await fetchOrders(false).catch((fallbackError) => {
                if (fallbackError instanceof AutopartsApiError) {
                    return createEmptyPageResult<OrderView>(page, pageSize)
                }
                throw fallbackError
            })

            return scopeSellerOrders(fallback, vendorId)
        }

        if (error instanceof AutopartsApiError) {
            return createEmptyPageResult<OrderView>(page, pageSize)
        }

        throw error
    }
}

function mergeSellerBuyerReviews(
    reviewGroups: BuyerReviewView[][]
): BuyerReviewView[] {
    const reviewsById = new Map<string, BuyerReviewView>()

    for (const review of reviewGroups.flat()) {
        const key =
            review.id ||
            `${review.targetType}:${review.targetId}:${review.buyerId}:${review.createdAt}`
        const previous = reviewsById.get(key)

        if (!previous) {
            reviewsById.set(key, review)
            continue
        }

        const mediaByKey = new Map(
            [...(previous.media ?? []), ...(review.media ?? [])].map((media) => [
                media.key,
                media,
            ])
        )
        const repliesById = new Map(
            [...(previous.replies ?? []), ...(review.replies ?? [])].map(
                (reply) => [reply.id, reply]
            )
        )

        reviewsById.set(key, {
            ...previous,
            ...review,
            review: review.review || previous.review,
            buyerName:
                review.buyerName !== "Verified buyer"
                    ? review.buyerName
                    : previous.buyerName,
            vendorId: review.vendorId || previous.vendorId,
            productId: review.productId || previous.productId,
            media: Array.from(mediaByKey.values()),
            replies: Array.from(repliesById.values()),
        })
    }

    return Array.from(reviewsById.values()).sort((left, right) => {
        const leftTime = new Date(left.createdAt).getTime()
        const rightTime = new Date(right.createdAt).getTime()

        if (Number.isNaN(leftTime) || Number.isNaN(rightTime)) {
            return 0
        }

        return rightTime - leftTime
    })
}

async function listSellerBuyerReviewsWithFallback(
    vendorId: string,
    products: ProductView[]
): Promise<BuyerReviewView[]> {
    const productIds = Array.from(
        new Set(
            products
                .map((product) => product.id?.trim())
                .filter((productId): productId is string => Boolean(productId))
        )
    )
    const productIdSet = new Set(productIds)
    const embeddedReviews = products.flatMap(
        (product) => product.buyerReviews ?? []
    )
    const [sellerReviews, shopReviews, ...productReviewGroups] =
        await Promise.all([
            autopartsApi.reviews.listSeller(vendorId).catch((error) => {
                if (error instanceof AutopartsApiError) {
                    return []
                }
                throw error
            }),
            autopartsApi.reviews.listShop(vendorId).catch((error) => {
                if (error instanceof AutopartsApiError) {
                    return []
                }
                throw error
            }),
            ...productIds.map((productId) =>
                autopartsApi.reviews
                    .listProduct(productId, vendorId)
                    .catch((error) => {
                        if (error instanceof AutopartsApiError) {
                            return []
                        }
                        throw error
                    })
            ),
        ])

    return mergeSellerBuyerReviews([
        embeddedReviews,
        sellerReviews,
        shopReviews,
        ...productReviewGroups,
    ]).filter((review) => {
        if (review.targetType === "shop") {
            return review.vendorId === vendorId || review.targetId === vendorId
        }

        const productId = review.productId || review.targetId
        return review.vendorId === vendorId || productIdSet.has(productId)
    })
}

async function loadSellerRouteData({
    request,
    productPageSize,
    orderPageSize,
    orderSummary = false,
    includeBuyerReviews = false,
}: {
    request?: Request
    productPageSize: number
    orderPageSize: number
    orderSummary?: boolean
    includeBuyerReviews?: boolean
}): Promise<SellerRouteData> {
    const query = request ? getPageQuery(request) : {}
    const { currentUser, vendorProfile } = await loadSellerContext()
    const vendorId = vendorProfile?.id

    const [products, orders] = await Promise.all([
        listSellerProductsWithFallback(query, vendorId, productPageSize),
        listSellerOrdersWithFallback(
            {
                page: 1,
                pageSize: orderPageSize,
                ...(query.search ? { search: query.search } : {}),
                ...(query.sortBy ? { sortBy: query.sortBy } : {}),
                ...(query.sortDirection
                    ? { sortDirection: query.sortDirection }
                    : {}),
            },
            vendorId,
            orderPageSize,
            orderSummary
        ),
    ])
    const buyerReviews =
        includeBuyerReviews && vendorId
            ? await listSellerBuyerReviewsWithFallback(
                  vendorId,
                  products.items
              )
            : []

    return {
        currentUser,
        vendorProfile,
        products,
        orders,
        buyerReviews,
    }
}

async function loadSellerDashboard({ request }: LoaderFunctionArgs) {
    return loadSellerRouteData({
        request,
        productPageSize: 12,
        orderPageSize: 8,
        orderSummary: true,
    })
}

async function loadSellerProducts({ request }: LoaderFunctionArgs) {
    return loadSellerRouteData({
        request,
        productPageSize: 12,
        orderPageSize: 200,
        orderSummary: true,
        includeBuyerReviews: true,
    })
}

async function loadSellerInventory() {
    const { currentUser, vendorProfile } = await loadSellerContext()
    const vendorId = vendorProfile?.id
    if (!vendorId) {
        return {
            currentUser,
            vendorProfile,
            products: createEmptyPageResult<ProductView>(1, 100),
        }
    }

    const inventoryItems: ProductView[] = []
    let page = 1
    let totalPages = 1

    do {
        const result = await listSellerProductsWithFallback(
            { page, pageSize: 100, sortBy: "updatedAt", sortDirection: "desc" },
            vendorId,
            100
        )
        inventoryItems.push(...result.items)
        totalPages = Math.max(1, result.totalPages || 1)
        page += 1
    } while (page <= totalPages && page <= 100)

    return {
        currentUser,
        vendorProfile,
        products: {
            items: inventoryItems,
            page: 1,
            pageSize: inventoryItems.length || 100,
            total: inventoryItems.length,
            totalPages: inventoryItems.length > 0 ? 1 : 0,
        },
    }
}

async function loadSellerConversations() {
    const { currentUser, vendorProfile } = await loadSellerContext()
    return { currentUser, vendorProfile }
}

async function loadSellerOrders({ request }: LoaderFunctionArgs) {
    return loadSellerRouteData({
        request,
        productPageSize: 200,
        orderPageSize: 10,
    })
}

async function loadSellerAnalytics(): Promise<SellerRouteData> {
    const { currentUser, vendorProfile } = await loadSellerContext()
    const vendorId = vendorProfile?.id

    if (!vendorId) {
        return {
            currentUser,
            vendorProfile,
            products: createEmptyPageResult<ProductView>(1, 100),
            orders: createEmptyPageResult<OrderView>(1, 100),
            buyerReviews: [],
        }
    }

    const collectProducts = async () => {
        const itemsById = new Map<string, ProductView>()
        let page = 1
        let totalPages = 1
        let useVendorScope = true

        do {
            let result: PageResult<ProductView>
            try {
                result = await autopartsApi.products.list<PageResult<ProductView>>({
                    page,
                    pageSize: 100,
                    sortBy: "updatedAt",
                    sortDirection: "desc",
                    ...(useVendorScope ? { vendorId } : {}),
                })
            } catch (error) {
                if (!(error instanceof AutopartsApiError) || !useVendorScope) {
                    if (error instanceof AutopartsApiError) break
                    throw error
                }
                useVendorScope = false
                page = 1
                totalPages = 1
                itemsById.clear()
                continue
            }

            const scoped = scopeSellerProducts(result, vendorId)
            scoped.items.forEach((product, index) => {
                itemsById.set(product.id || `${page}-${index}`, product)
            })
            totalPages = Math.max(1, result.totalPages || 1)
            page += 1
        } while (page <= totalPages && page <= 100)

        const items = Array.from(itemsById.values())
        return {
            items,
            page: 1,
            pageSize: items.length || 100,
            total: items.length,
            totalPages: items.length > 0 ? 1 : 0,
        }
    }

    const collectOrders = async () => {
        const itemsById = new Map<string, OrderView>()
        let page = 1
        let totalPages = 1
        let useVendorScope = true

        do {
            let result: PageResult<OrderView>
            try {
                result = await autopartsApi.orders.list<PageResult<OrderView>>({
                    page,
                    pageSize: 100,
                    summary: true,
                    ...(useVendorScope ? { vendorId } : {}),
                })
            } catch (error) {
                if (!(error instanceof AutopartsApiError) || !useVendorScope) {
                    if (error instanceof AutopartsApiError) break
                    throw error
                }
                useVendorScope = false
                page = 1
                totalPages = 1
                itemsById.clear()
                continue
            }

            const scoped = scopeSellerOrders(result, vendorId)
            scoped.items.forEach((order, index) => {
                itemsById.set(order.id || `${page}-${index}`, order)
            })
            totalPages = Math.max(1, result.totalPages || 1)
            page += 1
        } while (page <= totalPages && page <= 100)

        const items = Array.from(itemsById.values())
        return {
            items,
            page: 1,
            pageSize: items.length || 100,
            total: items.length,
            totalPages: items.length > 0 ? 1 : 0,
        }
    }

    const [products, orders] = await Promise.all([
        collectProducts(),
        collectOrders(),
    ])

    return {
        currentUser,
        vendorProfile,
        products,
        orders,
        buyerReviews: [],
    }
}

async function loadQualityAssessorProducts() {
    let currentUser = await getCurrentSessionUser()
    if (currentUser?.id) {
        try {
            const freshUser = await autopartsApi.users.getById<UserView>(currentUser.id)
            currentUser = {
                ...currentUser,
                ...freshUser,
                role: "quality_assessor",
            }
            updateStoredAuthSessionUser(currentUser)
        } catch (error) {
            if (!(error instanceof AutopartsApiError)) throw error
        }
    }

    let products = createEmptyPageResult<ProductView>(1, 200)
    try {
        products = await autopartsApi.products.list<PageResult<ProductView>>({
            page: 1,
            pageSize: 200,
            listingState: "SUBMITTED",
            sortBy: "updatedAt",
            sortDirection: "desc",
        })
    } catch (error) {
        if (!(error instanceof AutopartsApiError)) throw error
    }

    return { currentUser, products }
}

async function loadQualityAssessorProfits() {
    const currentUser = await getCurrentSessionUser()
    return {
        currentUser,
        orders: createEmptyPageResult<OrderView>(1, 1),
    }
}

async function loadCourierDeliveries() {
    let currentUser = await getCurrentSessionUser()
    let orders = createEmptyPageResult<OrderView>(1, 200)

    if (currentUser?.id) {
        try {
            const freshUser = await autopartsApi.users.getById<UserView>(
                currentUser.id
            )
            currentUser = {
                ...currentUser,
                ...freshUser,
                id: freshUser.id || currentUser.id,
                name: freshUser.name || currentUser.name,
                email: freshUser.email || currentUser.email,
                role: "courier",
            }
            updateStoredAuthSessionUser(currentUser)
        } catch (error) {
            if (!(error instanceof AutopartsApiError)) {
                throw error
            }
        }
    }

    try {
        orders = await autopartsApi.orders.list<PageResult<OrderView>>({
            page: 1,
            pageSize: 200,
        })
    } catch (error) {
        if (!(error instanceof AutopartsApiError)) {
            throw error
        }
    }

    return {
        currentUser,
        orders,
    }
}

async function loadCourierProfits() {
    const currentUser = await getCurrentSessionUser()
    let orders = createEmptyPageResult<OrderView>(1, 1000)

    try {
        orders = await autopartsApi.orders.list<PageResult<OrderView>>({
            page: 1,
            pageSize: 1000,
            summary: true,
        })
    } catch (error) {
        if (!(error instanceof AutopartsApiError)) throw error
    }

    return { currentUser, orders }
}

async function loadAdminProfits() {
    const currentUser = await getCurrentSessionUser()
    let orders = createEmptyPageResult<OrderView>(1, 1000)

    try {
        orders = await autopartsApi.orders.list<PageResult<OrderView>>({
            page: 1,
            pageSize: 1000,
            summary: true,
        })
    } catch (error) {
        if (!(error instanceof AutopartsApiError)) throw error
    }

    return { currentUser, orders }
}

async function loadAccountSettings() {
    const currentUser = await getCurrentSessionUser()
    let user: UserView | null = null

    if (currentUser?.id) {
        try {
            user = await autopartsApi.users.getById<UserView>(currentUser.id)
            if (user) {
                updateStoredAuthSessionUser({ ...currentUser, ...user })
            }
        } catch (error) {
            if (!(error instanceof AutopartsApiError)) {
                throw error
            }
        }
    }

    return {
        currentUser: user ? { ...currentUser, ...user } : currentUser,
        user,
    }
}

async function loadCourierSettings() {
    return loadAccountSettings()
}

async function loadSellerProfile() {
    return loadAccountSettings()
}

async function loadAdminSettings() {
    return loadAccountSettings()
}

async function loadAdminDashboard() {
    return refreshMarketplaceData()
}

async function loadUsers({ request }: LoaderFunctionArgs) {
    return autopartsApi.users.list(getPageQuery(request))
}

function getErrorMessage(error: unknown): {
    title: string
    description: string
    status?: number
} {
    if (error instanceof AutopartsApiError) {
        return {
            title: error.message || "API request failed",
            description: `Unable to load data from ${autopartsApi.health.getBaseUrl()}.`,
            status: error.status,
        }
    }

    if (isRouteErrorResponse(error)) {
        return {
            title: error.statusText || "Route error",
            description:
                typeof error.data === "string"
                    ? error.data
                    : `Unable to complete the request using ${autopartsApi.health.getBaseUrl()}.`,
            status: error.status,
        }
    }

    if (isRouteChunkLoadError(error)) {
        return {
            title: "SpareLynk needs to refresh",
            description:
                "The app was updated while this page was open. Reload to continue with the latest version.",
        }
    }

    if (error instanceof Error) {
        return {
            title: "This page could not be loaded",
            description:
                "Reload the page to try again, or return to the welcome page.",
        }
    }

    return {
        title: "Unexpected error",
        description: `Something went wrong while loading data from ${autopartsApi.health.getBaseUrl()}.`,
    }
}

function RouteErrorBoundary() {
    const error = useRouteError()
    const location = useLocation()
    const { title, description, status } = getErrorMessage(error)
    const isChunkError = isRouteChunkLoadError(error)

    return (
        <>
            <Seo
                title={`${typeof status === "number" ? `Error ${status}: ` : ""}${title} | SpareLynk`}
                description={description}
                pathname={location.pathname}
                index={false}
                follow
            />
            <main className="flex min-h-[calc(100svh-5rem)] items-center justify-center px-4 py-8 sm:px-6">
            <Card className="w-full max-w-xl overflow-hidden border-border/70 shadow-xl shadow-black/5">
                <CardHeader className="items-center px-5 pb-3 pt-8 text-center sm:px-8 sm:pt-10">
                    <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                        <AlertCircle className="h-6 w-6" aria-hidden="true" />
                    </div>
                    {typeof status === "number" ? (
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                            Error {status}
                        </p>
                    ) : null}
                    <CardTitle className="text-xl sm:text-2xl">{title}</CardTitle>
                    <CardDescription className="max-w-md text-sm leading-6">
                        {description}
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-2 px-5 pb-8 sm:flex-row sm:justify-center sm:px-8 sm:pb-10">
                    <Button
                        className="w-full sm:w-auto"
                        onClick={() => window.location.reload()}
                    >
                        <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                        {isChunkError ? "Reload app" : "Try again"}
                    </Button>
                    <Button
                        variant="outline"
                        className="w-full sm:w-auto"
                        onClick={() => window.location.assign("/")}
                    >
                        <Home className="mr-2 h-4 w-4" aria-hidden="true" />
                        Welcome page
                    </Button>
                </CardContent>
            </Card>
            </main>
        </>
    )
}

export const router = createBrowserRouter([
    {
        path: "/",
        lazy: lazyRouteComponent(() => import("./pages/welcome"), "Welcome"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
        loader: loadWelcome,
    },
    {
        path: "/about",
        lazy: lazyRouteComponent(() => import("./pages/about"), "About"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
        loader: loadPublicPlatformData,
    },
    {
        path: "/contact",
        lazy: lazyRouteComponent(() => import("./pages/contact"), "Contact"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
        loader: loadPublicPlatformData,
    },
    {
        path: "/terms",
        lazy: lazyRouteComponent(() => import("./pages/terms"), "Terms"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
    },
    {
        path: "/privacy",
        lazy: lazyRouteComponent(() => import("./pages/privacy"), "Privacy"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
    },
    {
        path: "/auth/login",
        lazy: lazyRouteComponent(() => import("./pages/auth/login"), "Login"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
        loader: createGuardedLoader(undefined, { guestOnly: true }),
    },
    {
        path: "/auth/signup",
        lazy: lazyRouteComponent(() => import("./pages/auth/signup"), "Signup"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
        loader: createGuardedLoader(undefined, { guestOnly: true }),
    },
    {
        path: "/auth/forgot-password",
        lazy: lazyRouteComponent(() => import("./pages/auth/forgot-password"), "ForgotPassword"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
        loader: createGuardedLoader(undefined, { guestOnly: true }),
    },
    {
        path: "/auth/verify-email",
        lazy: lazyRouteComponent(() => import("./pages/auth/verify-email"), "VerifyEmail"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
    },
    {
        path: "/auth/callback",
        lazy: lazyRouteComponent(() => import("./pages/auth/callback"), "Callback"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
    },
    {
        path: "/auth/reset-password",
        lazy: lazyRouteComponent(() => import("./pages/auth/reset-password"), "ResetPassword"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
    },
    {
        path: "/auth/change-password",
        lazy: lazyRouteComponent(() => import("./pages/auth/change-password"), "ChangePassword"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
        loader: createGuardedLoader(undefined, {
            allowedRoles: ["buyer", "seller", "courier", "quality_assessor", "admin"],
        }),
    },

    // Buyer Routes
    {
        path: "/buyer",
        lazy: lazyRouteComponent(() => import("./layouts/buyer-layout"), "BuyerLayout"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
        shouldRevalidate: shouldRevalidateMessagePanelNavigation,
        children: [
            {
                index: true,
                loader: () => redirect("/buyer/home"),
            },
            {
                path: "home",
                lazy: lazyRouteComponent(() => import("./pages/buyer/home"), "BuyerHome"),
                loader: loadBuyerHome,
                shouldRevalidate: shouldRevalidateMessagePanelNavigation,
            },
            {
                path: "products",
                lazy: lazyRouteComponent(() => import("./pages/buyer/products"), "BuyerProducts"),
                loader: loadBuyerProducts,
                shouldRevalidate: shouldRevalidateMessagePanelNavigation,
            },
            {
                path: "products/:id",
                lazy: lazyRouteComponent(() => import("./pages/buyer/product-detail"), "BuyerProductDetail"),
                loader: loadProductById,
                shouldRevalidate: shouldRevalidateMessagePanelNavigation,
            },
            {
                path: "requests",
                lazy: lazyRouteComponent(() => import("./pages/part-requests"), "BuyerPartRequests"),
                loader: createGuardedLoader(undefined, {
                    allowedRoles: ["buyer"],
                }),
            },
            {
                path: "shops/:vendorId",
                lazy: lazyRouteComponent(() => import("./pages/buyer/shop-detail"), "BuyerShopDetail"),
                loader: loadBuyerShop,
                shouldRevalidate: shouldRevalidateMessagePanelNavigation,
            },
            {
                path: "cart",
                lazy: lazyRouteComponent(() => import("./pages/buyer/cart"), "BuyerCart"),
                loader: createGuardedLoader(undefined, {
                    allowedRoles: ["buyer"],
                }),
                shouldRevalidate: shouldRevalidateMessagePanelNavigation,
            },
            {
                path: "checkout",
                lazy: lazyRouteComponent(() => import("./pages/buyer/checkout"), "BuyerCheckout"),
                loader: createGuardedLoader(undefined, {
                    allowedRoles: ["buyer"],
                }),
                shouldRevalidate: shouldRevalidateMessagePanelNavigation,
            },
            {
                path: "conversations",
                lazy: lazyRouteComponent(() => import("./pages/buyer/conversations"), "BuyerConversations"),
                loader: createGuardedLoader(loadBuyerConversations, {
                    allowedRoles: ["buyer"],
                }),
                shouldRevalidate: shouldRevalidateMessagePanelNavigation,
            },
            {
                path: "orders",
                lazy: lazyRouteComponent(() => import("./pages/buyer/orders"), "BuyerOrders"),
                loader: createGuardedLoader(loadOrders, {
                    allowedRoles: ["buyer"],
                }),
                shouldRevalidate: shouldRevalidateMessagePanelNavigation,
            },
            {
                path: "orders/:id",
                lazy: lazyRouteComponent(() => import("./pages/buyer/orders"), "BuyerOrders"),
                loader: createGuardedLoader(loadOrderById, {
                    allowedRoles: ["buyer"],
                }),
                shouldRevalidate: shouldRevalidateMessagePanelNavigation,
            },
            {
                path: "reports",
                lazy: lazyRouteComponent(() => import("./pages/buyer/reports"), "BuyerReports"),
                loader: createGuardedLoader(loadBuyerReports, {
                    allowedRoles: ["buyer"],
                }),
                shouldRevalidate: shouldRevalidateMessagePanelNavigation,
            },
            {
                path: "support",
                lazy: lazyRouteComponent(() => import("./pages/shared/support-chat"), "BuyerSupport"),
                loader: createGuardedLoader(undefined, {
                    allowedRoles: ["buyer"],
                }),
            },
            {
                path: "wallet",
                lazy: lazyRouteComponent(() => import("./pages/buyer/wallet"), "BuyerWallet"),
                loader: createGuardedLoader(async () => ({
                    currentUser: await getCurrentSessionUser(),
                }), {
                    allowedRoles: ["buyer"],
                }),
                shouldRevalidate: shouldRevalidateMessagePanelNavigation,
            },
            {
                path: "wishlist",
                lazy: lazyRouteComponent(() => import("./pages/buyer/wishlist"), "BuyerWishlist"),
                loader: createGuardedLoader(loadBuyerWishlist, {
                    allowedRoles: ["buyer"],
                }),
                shouldRevalidate: shouldRevalidateMessagePanelNavigation,
            },
            {
                path: "profile",
                lazy: lazyRouteComponent(() => import("./pages/buyer/profile"), "BuyerProfile"),
                loader: createGuardedLoader(loadBuyerProfile, {
                    allowedRoles: ["buyer"],
                }),
                shouldRevalidate: shouldRevalidateMessagePanelNavigation,
            },
        ],
    },

    // Seller Routes
    {
        path: "/seller",
        lazy: lazyRouteComponent(() => import("./layouts/seller-layout"), "SellerLayout"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
        loader: createGuardedLoader(undefined, {
            allowedRoles: ["seller"],
        }),
        shouldRevalidate: shouldRevalidateMessagePanelNavigation,
        children: [
            {
                index: true,
                loader: createGuardedLoader(
                    () => redirect("/seller/dashboard"),
                    { allowedRoles: ["seller"] }
                ),
            },
            {
                path: "dashboard",
                lazy: lazyRouteComponent(() => import("./pages/seller/dashboard"), "SellerDashboard"),
                loader: createGuardedLoader(loadSellerDashboard, {
                    allowedRoles: ["seller"],
                }),
                shouldRevalidate: shouldRevalidateMessagePanelNavigation,
            },
            {
                path: "products",
                lazy: lazyRouteComponent(() => import("./pages/seller/products"), "SellerProducts"),
                loader: createGuardedLoader(loadSellerProducts, {
                    allowedRoles: ["seller"],
                }),
            },
            {
                path: "products/new",
                lazy: lazyRouteComponent(() => import("./pages/seller/products"), "SellerProducts"),
                loader: createGuardedLoader(loadSellerProducts, {
                    allowedRoles: ["seller"],
                }),
            },
            {
                path: "inventory",
                lazy: lazyRouteComponent(() => import("./pages/seller/inventory"), "SellerInventory"),
                loader: createGuardedLoader(loadSellerInventory, {
                    allowedRoles: ["seller"],
                }),
            },
            {
                path: "warehouse",
                lazy: lazyRouteComponent(() => import("./pages/seller/warehouse"), "SellerWarehouse"),
                loader: createGuardedLoader(undefined, {
                    allowedRoles: ["seller"],
                }),
            },
            {
                path: "requests",
                lazy: lazyRouteComponent(() => import("./pages/part-requests"), "SellerPartRequests"),
                loader: createGuardedLoader(undefined, {
                    allowedRoles: ["seller"],
                }),
            },
            {
                path: "conversations",
                lazy: lazyRouteComponent(() => import("./pages/seller/conversations"), "SellerConversations"),
                loader: createGuardedLoader(loadSellerConversations, {
                    allowedRoles: ["seller"],
                }),
            },
            {
                path: "orders",
                lazy: lazyRouteComponent(() => import("./pages/seller/orders"), "SellerOrders"),
                loader: createGuardedLoader(loadSellerOrders, {
                    allowedRoles: ["seller"],
                }),
            },
            {
                path: "analytics",
                lazy: lazyRouteComponent(() => import("./pages/seller/analytics"), "SellerAnalytics"),
                loader: createGuardedLoader(loadSellerAnalytics, {
                    allowedRoles: ["seller"],
                }),
            },
            {
                path: "profits",
                lazy: lazyRouteComponent(() => import("./pages/seller/profits"), "SellerProfits"),
                loader: createGuardedLoader(loadSellerAnalytics, {
                    allowedRoles: ["seller"],
                }),
            },
            {
                path: "services/*",
                lazy: lazyRouteComponent(() => import("./pages/seller/services"), "SellerServices"),
                loader: createGuardedLoader(undefined, {
                    allowedRoles: ["seller"],
                }),
                shouldRevalidate: shouldRevalidateMessagePanelNavigation,
            },
            {
                path: "support",
                lazy: lazyRouteComponent(() => import("./pages/shared/support-chat"), "SellerSupport"),
                loader: createGuardedLoader(undefined, {
                    allowedRoles: ["seller"],
                }),
            },
            {
                path: "profile",
                lazy: lazyRouteComponent(() => import("./pages/seller/profile"), "SellerProfile"),
                loader: createGuardedLoader(loadSellerProfile, {
                    allowedRoles: ["seller"],
                }),
            },
        ],
    },

    // Quality Assessor Routes
    {
        path: "/quality-assessor",
        lazy: lazyRouteComponent(() => import("./layouts/quality-assessor-layout"), "QualityAssessorLayout"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
        loader: createGuardedLoader(undefined, {
            allowedRoles: ["quality_assessor"],
        }),
        children: [
            {
                index: true,
                loader: createGuardedLoader(
                    () => redirect("/quality-assessor/products"),
                    { allowedRoles: ["quality_assessor"] }
                ),
            },
            {
                path: "products",
                lazy: lazyRouteComponent(() => import("./pages/quality-assessor/products"), "QualityAssessorProducts"),
                loader: createGuardedLoader(loadQualityAssessorProducts, {
                    allowedRoles: ["quality_assessor"],
                }),
            },
            {
                path: "reports",
                lazy: lazyRouteComponent(() => import("./pages/quality-assessor/reports"), "QualityAssessorReports"),
                loader: createGuardedLoader(undefined, {
                    allowedRoles: ["quality_assessor"],
                }),
            },
            {
                path: "profits",
                lazy: lazyRouteComponent(() => import("./pages/quality-assessor/profits"), "QualityAssessorProfits"),
                loader: createGuardedLoader(loadQualityAssessorProfits, {
                    allowedRoles: ["quality_assessor"],
                }),
            },
            {
                path: "settings",
                lazy: lazyRouteComponent(() => import("./pages/quality-assessor/settings"), "QualityAssessorSettings"),
                loader: createGuardedLoader(loadAccountSettings, {
                    allowedRoles: ["quality_assessor"],
                }),
            },
        ],
    },

    // Courier Routes
    {
        path: "/courier",
        lazy: lazyRouteComponent(() => import("./layouts/courier-layout"), "CourierLayout"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
        loader: createGuardedLoader(undefined, {
            allowedRoles: ["courier"],
        }),
        children: [
            {
                index: true,
                loader: createGuardedLoader(
                    () => redirect("/courier/deliveries"),
                    { allowedRoles: ["courier"] }
                ),
            },
            {
                path: "deliveries",
                lazy: lazyRouteComponent(() => import("./pages/courier/deliveries"), "CourierDeliveries"),
                loader: createGuardedLoader(loadCourierDeliveries, {
                    allowedRoles: ["courier"],
                }),
            },
            {
                path: "profits",
                lazy: lazyRouteComponent(() => import("./pages/courier/profits"), "CourierProfits"),
                loader: createGuardedLoader(loadCourierProfits, {
                    allowedRoles: ["courier"],
                }),
            },
            {
                path: "reports",
                lazy: lazyRouteComponent(() => import("./pages/courier/reports"), "CourierReports"),
                loader: createGuardedLoader(undefined, {
                    allowedRoles: ["courier"],
                }),
            },
            {
                path: "settings",
                lazy: lazyRouteComponent(() => import("./pages/courier/settings"), "CourierSettings"),
                loader: createGuardedLoader(loadCourierSettings, {
                    allowedRoles: ["courier"],
                }),
            },
        ],
    },

    // Admin Routes
    {
        path: "/admin",
        lazy: lazyRouteComponent(() => import("./layouts/admin-layout"), "AdminLayout"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
        loader: createGuardedLoader(undefined, {
            allowedRoles: ["admin"],
        }),
        children: [
            {
                index: true,
                loader: createGuardedLoader(
                    () => redirect("/admin/dashboard"),
                    { allowedRoles: ["admin"] }
                ),
            },
            {
                path: "dashboard",
                lazy: lazyRouteComponent(() => import("./pages/admin/dashboard"), "AdminDashboard"),
                loader: createGuardedLoader(loadAdminDashboard, {
                    allowedRoles: ["admin"],
                }),
            },
            {
                path: "users",
                lazy: lazyRouteComponent(() => import("./pages/admin/users"), "AdminUsers"),
                loader: createGuardedLoader(loadUsers, {
                    allowedRoles: ["admin"],
                }),
            },
            {
                path: "products",
                lazy: lazyRouteComponent(() => import("./pages/admin/products"), "AdminProducts"),
                loader: createGuardedLoader(loadAdminProducts, {
                    allowedRoles: ["admin"],
                }),
            },
            {
                path: "warehouse",
                lazy: lazyRouteComponent(() => import("./pages/admin/warehouse"), "AdminWarehouse"),
                loader: createGuardedLoader(undefined, {
                    allowedRoles: ["admin"],
                }),
            },
            {
                path: "requests",
                lazy: lazyRouteComponent(() => import("./pages/part-requests"), "AdminPartRequests"),
                loader: createGuardedLoader(undefined, {
                    allowedRoles: ["admin"],
                }),
            },
            {
                path: "profits",
                lazy: lazyRouteComponent(() => import("./pages/admin/profits"), "AdminProfits"),
                loader: createGuardedLoader(loadAdminProfits, {
                    allowedRoles: ["admin"],
                }),
            },
            {
                path: "reports",
                lazy: lazyRouteComponent(() => import("./pages/admin/reports"), "AdminReports"),
                loader: createGuardedLoader(undefined, {
                    allowedRoles: ["admin"],
                }),
            },
            {
                path: "account-recovery",
                lazy: lazyRouteComponent(() => import("./pages/admin/account-recovery"), "AdminAccountRecovery"),
                loader: createGuardedLoader(undefined, {
                    allowedRoles: ["admin"],
                }),
            },
            {
                path: "support",
                lazy: lazyRouteComponent(() => import("./pages/shared/support-chat"), "AdminSupport"),
                loader: createGuardedLoader(undefined, {
                    allowedRoles: ["admin"],
                }),
            },
            {
                path: "settings",
                lazy: lazyRouteComponent(() => import("./pages/admin/settings"), "AdminSettings"),
                loader: createGuardedLoader(loadAdminSettings, {
                    allowedRoles: ["admin"],
                }),
            },
        ],
    },

    {
        path: "*",
        lazy: lazyRouteComponent(() => import("./pages/notfound"), "NotFound"),
        ErrorBoundary: RouteErrorBoundary,
        HydrateFallback: RouterHydrateFallback,
    },
])

const routeModulePreloads = new Map<string, Promise<void>>()

export function preloadRouteModules(href: string): Promise<void> {
    if (typeof window === "undefined") {
        return Promise.resolve()
    }

    let url: URL
    try {
        url = new URL(href, window.location.origin)
    } catch {
        return Promise.resolve()
    }

    if (url.origin !== window.location.origin) {
        return Promise.resolve()
    }

    const matches = matchRoutes(router.routes, url.pathname) ?? []
    const cacheKey =
        matches
            .map(({ route }) =>
                String(route.id ?? route.path ?? (route.index ? "index" : "route"))
            )
            .join("|") || url.pathname
    const existing = routeModulePreloads.get(cacheKey)
    if (existing) {
        return existing
    }

    const preload = Promise.all(
        matches.map(({ route }) => {
            const lazy = route.lazy

            return typeof lazy === "function"
                ? Promise.resolve(lazy()).then(() => undefined)
                : Promise.resolve()
        })
    )
        .then(() => undefined)
        .catch((error) => {
            routeModulePreloads.delete(cacheKey)
            throw error
        })

    routeModulePreloads.set(cacheKey, preload)
    return preload
}
